<script setup lang="ts">
import type { SearchableTagIdentifiers } from 'lc-repository/entities'
import type { SearchClientHouse } from '~/types/search/types'

const {
  colsDesktop,
  favoritePeriod,
  isFavoritePeriodWeekly,
  mapVisible,
  nearPeriods,
  searchableTagsIdentifiers,
} = defineProps<{
  colsDesktop: '6' | '8' | '12'
  favoritePeriod: { percent: number; period: string }
  isFavoritePeriodWeekly: boolean
  mapVisible: boolean
  nearPeriods?: { previous: string; next: string }
  searchableTagsIdentifiers?: SearchableTagIdentifiers[] | null
}>()
defineEmits<{
  'click-favorite-period': ['previous' | 'next']
  paginate: ['previous-page' | 'next-page']
}>()
const hoveredHouseId = defineModel<number | undefined>()

const { isDesktopOrTablet, isLargeScreen } = useBreakpoint()
const { startDate, endDate, hasDates } = useSearchDateFilters()
const { currentReplica } = useSearchReplicaFilters()
const {
  algoliaQueryId,
  hasOnlyInvalidResults,
  invalidHouses,
  isLoading,
  showPreviousButton,
  page,
  nbPages,
} = useSearchResults({ startDate, endDate })
const { formattedHits } = useSearchClientResults({ hasDates })
const { trackAlgoliaClick } = useAlgoliaSearchInsights()
const { trackSearchHouse } = useSearchTracking()
const { setCurrentHouseFavorite } = useSearchWishlist()

const showPaginate = computed(() => nbPages.value > page.value + 1)
const numberPlaceholderProducts = computed(() => {
  if (!isDesktopOrTablet) return 2
  switch (colsDesktop) {
    case '12':
      return 4

    case '8':
      return 6

    default:
      return 8
  }
})

const setFavoriteHouse = (house: SearchClientHouse) => {
  if (house) {
    const price = house.currentPrice?.isValid
      ? house.currentPrice.value
      : undefined

    setCurrentHouseFavorite(
      {
        id: house.id,
        name: house.name,
        iconicCollection: house.iconicCollection,
        publicPrice: price || undefined,
      },
      'list',
    )
  }
}

const showHouseCardInfo = (houseId: number) => {
  if (isFavoritePeriodWeekly) return false
  const twoCardsPerLine =
    !isLargeScreen.value && invalidHouses.value.first === houseId
  const threeCardsPerLine =
    isLargeScreen.value && invalidHouses.value.second
      ? Number(invalidHouses.value.second) === Number(houseId)
      : Number(invalidHouses.value.first) === Number(houseId)

  return threeCardsPerLine || twoCardsPerLine
}

const trackHouse = (houseId: number) => {
  trackSearchHouse({
    houseId,
    mapVisible: mapVisible,
    searchableTagsIdentifiers: searchableTagsIdentifiers || [],
  })
}

const handleClick = (houseId: number, position: number) => {
  trackHouse(houseId)

  trackAlgoliaClick({
    indexName: currentReplica.value.value,
    objectIDs: [houseId.toString()],
    positions: position ? [position] : undefined,
    queryID: algoliaQueryId.value,
  })
}
</script>

<template>
  <div>
    <SearchListLoadPreviousButton
      v-if="showPreviousButton"
      :page="page"
      @previous-page="$emit('paginate', 'previous-page')"
    />

    <BaseRow v-if="isLoading" class="mb-6">
      <BaseCol
        v-for="n in numberPlaceholderProducts"
        :key="`base-house-card-placeholder-${n}`"
        class="mb-4"
        cols="24"
        md="12"
        :xl="colsDesktop"
      >
        <BaseHouseCardPlaceholder />
      </BaseCol>
    </BaseRow>

    <BaseRow class="mb-2">
      <template
        v-for="(house, index) in formattedHits"
        :key="`house-${house.id}`"
      >
        <template
          v-if="
            !isLoading &&
            startDate &&
            endDate &&
            invalidHouses.first === house.id
          "
        >
          <BaseCol
            v-if="isFavoritePeriodWeekly"
            class="px-0 sm:mb-4 sm:px-2"
            cols="24"
            md="12"
            :xl="colsDesktop"
          >
            <SearchFavoritePeriod
              class="flex h-full flex-col justify-around rounded bg-beige px-4 py-6 sm:p-6"
              :favorite-period="favoritePeriod"
              :near-periods="nearPeriods"
              @click-favorite-period="$emit('click-favorite-period', $event)"
            />
          </BaseCol>

          <h2
            :class="[
              'mb-8 w-24/24 px-2 text-4xl',
              hasOnlyInvalidResults ? 'mt-4 sm:mt-8' : 'mt-8 sm:mt-16',
            ]"
          >
            {{ $t('search.invalidPeriodHouses.otherAvailableHouses') }}
          </h2>
        </template>

        <BaseCol
          class="mb-6 px-0 sm:px-2 md:mb-4"
          cols="24"
          md="12"
          :xl="colsDesktop"
        >
          <SearchHouseCard
            :id="house.id"
            :bathrooms="house.bathrooms"
            :bedrooms="house.bedrooms"
            :capacity="house.capacity"
            :destination="house.destination"
            :photos="house.photos"
            :price="house.currentPrice.text"
            :slug="house.slug"
            :title="house.name"
            @handle-click="handleClick(house.id, index + 1)"
            @mouseover="hoveredHouseId = house.id"
            @mouseleave="hoveredHouseId = undefined"
          >
            <template #card-action>
              <WishlistToggle
                v-slot="slotProps"
                :house-id="house.id"
                :house-name="house.name"
                @callback-click="setFavoriteHouse(house)"
              >
                <HouseWishlistButton :is-active="slotProps.active" />
              </WishlistToggle>
            </template>

            <template #before-price>
              <div class="mt-3 flex min-h-7 gap-2">
                <span
                  v-for="keyfeature in house.highlightKeyFeatures"
                  :key="keyfeature.identifier"
                  class="max-w-[50%] truncate rounded bg-gray-100 px-2 py-1 text-xs"
                >
                  {{ keyfeature.name }}
                </span>
              </div>
            </template>

            <template #card-content>
              <div class="absolute left-4 top-2">
                <div class="flex text-center">
                  <div
                    v-if="house.iconicCollection"
                    data-testid="house-card__iconic"
                    class="z-20 mr-2 rounded bg-successBg px-2 py-1 text-xs"
                  >
                    {{ $t('global.iconic') }}
                  </div>
                </div>
              </div>
            </template>
          </SearchHouseCard>
        </BaseCol>

        <BaseCol
          v-if="showHouseCardInfo(house.id)"
          class="mb-6 px-0 sm:mb-4 sm:px-2"
          cols="24"
          md="12"
          :xl="colsDesktop"
        >
          <BaseHouseCardInfo />
        </BaseCol>
        <BaseCol
          v-else-if="!isFavoritePeriodWeekly && index === 4"
          class="mb-6 px-0 sm:mb-4 sm:px-2"
          cols="24"
          md="12"
          :xl="colsDesktop"
        >
          <SearchScheduleACall
            class="size-full rounded bg-primary-100 px-5 py-4"
          />
        </BaseCol>
      </template>
    </BaseRow>

    <SearchListLoadMoreButton
      :loading="isLoading"
      :page="page"
      :show-paginate="showPaginate"
      @next-page="$emit('paginate', 'next-page')"
    />
  </div>
</template>
